import { lazy, Suspense } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { PageLoader } from '@navegasistemas/dls-navega-rjs'
import { ProtectedRoute } from '@/components/protected-route'
import { RequirePasswordChangeRoute } from '@/components/require-password-change-route'
import { GuestRoute } from '@/components/guest-route'
import { AppLayout } from '@/layouts/app-layout'

const LoginPage = lazy(() => import('@/pages/login-page'))
const ForgotPasswordPage = lazy(() => import('@/pages/forgot-password-page'))
const ResetPasswordPage = lazy(() => import('@/pages/reset-password-page'))
const ChangePasswordPage = lazy(() => import('@/pages/change-password-page'))
const DashboardPage = lazy(() => import('@/pages/dashboard-page'))
const MobileAppsPage = lazy(() => import('@/pages/mobile-apps-page'))
const MobileAppDetailPage = lazy(() => import('@/pages/mobile-app-detail-page'))
const UsersPage = lazy(() => import('@/pages/users-page'))
const RolesPage = lazy(() => import('@/pages/roles-page'))
const DeployHubPage = lazy(() => import('@/pages/deploy-hub-page'))
const DeployHubClientsPage = lazy(() => import('@/pages/deploy-hub-clients-page'))
const DeployHubClientDetailPage = lazy(() => import('@/pages/deploy-hub-client-detail-page'))
const DeployHubApplicationsPage = lazy(() => import('@/pages/deploy-hub-applications-page'))
const DeployHubApplicationDetailPage = lazy(
  () => import('@/pages/deploy-hub-application-detail-page'),
)
const DeployHubReleasesPage = lazy(() => import('@/pages/deploy-hub-releases-page'))
const DeployHubDeploymentsPage = lazy(() => import('@/pages/deploy-hub-deployments-page'))
const DeployHubDeploymentDetailPage = lazy(
  () => import('@/pages/deploy-hub-deployment-detail-page'),
)
const DeployHubEnvSyncPage = lazy(() => import('@/pages/deploy-hub-env-sync-page'))
const DeployHubEnvSyncDetailPage = lazy(() => import('@/pages/deploy-hub-env-sync-detail-page'))
const DeployHubLogsPage = lazy(() => import('@/pages/deploy-hub-logs-page'))
const DeployHubLicensesPage = lazy(() => import('@/pages/deploy-hub-licenses-page'))

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Guest-only routes — redirect to /dashboard when already authenticated */}
          <Route element={<GuestRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          </Route>

          {/*
            RF08: rota pública, fora de GuestRoute/ProtectedRoute. O link do
            e-mail de reset não sabe (e não deve se importar) se quem clicou
            está logado neste navegador — um usuário autenticado em uma aba
            pode estar redefinindo a senha por ter esquecido em outro
            dispositivo. Colocar em GuestRoute bloquearia esse caso (redirect
            para /dashboard); colocar em ProtectedRoute exigiria sessão ativa,
            o que contradiz o próprio propósito do fluxo (token de uso único
            da URL é a única credencial necessária).
          */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Authenticated routes */}
          <Route element={<ProtectedRoute />}>
            {/* RF06: enquanto mustChangePassword=true, bloqueia tudo além de /change-password */}
            <Route element={<RequirePasswordChangeRoute />}>
              <Route element={<AppLayout />}>
                <Route path="/dashboard" element={<DashboardPage />} />
                <Route path="/mobile-apps" element={<MobileAppsPage />} />
                <Route path="/mobile-apps/:id" element={<MobileAppDetailPage />} />
                <Route path="/users" element={<UsersPage />} />
                <Route path="/roles" element={<RolesPage />} />
                <Route path="/deploy-hub" element={<DeployHubPage />} />
                <Route path="/deploy-hub/clients" element={<DeployHubClientsPage />} />
                <Route path="/deploy-hub/clients/:id" element={<DeployHubClientDetailPage />} />
                <Route path="/deploy-hub/applications" element={<DeployHubApplicationsPage />} />
                <Route
                  path="/deploy-hub/applications/:appId"
                  element={<DeployHubApplicationDetailPage />}
                />
                <Route
                  path="/deploy-hub/applications/:appId/releases"
                  element={<DeployHubReleasesPage />}
                />
                <Route path="/deploy-hub/deployments" element={<DeployHubDeploymentsPage />} />
                <Route
                  path="/deploy-hub/deployments/:id"
                  element={<DeployHubDeploymentDetailPage />}
                />
                <Route path="/deploy-hub/env-sync" element={<DeployHubEnvSyncPage />} />
                <Route path="/deploy-hub/env-sync/:id" element={<DeployHubEnvSyncDetailPage />} />
                <Route path="/deploy-hub/logs" element={<DeployHubLogsPage />} />
                <Route path="/deploy-hub/licenses" element={<DeployHubLicensesPage />} />
                <Route path="/change-password" element={<ChangePasswordPage />} />
              </Route>
            </Route>
          </Route>

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
